import { API } from './api';
import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { Class, ClassProvider } from './class';
import { FrameProvider } from './frame';
import { TranslateService } from '@ngx-translate/core';

export interface SceneRecognitionPrediction {
    classObject: Class;
    probability: number;
    stars: number;
}

export interface DetectionPrediction extends DetectionBox {
    confidence: number;
}

export type DetectionGroundTruth = DetectionBox;

export interface DetectionBox {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    classObject: Class;
}

export interface DetectionValdiationFrameDetails {
    name: string;
    thumbSignedUrl: string;
    signedUrl: string;
    groundTruths: DetectionGroundTruth[];
    predictions: DetectionPrediction[];
}

/** An SVG path object derived from the metadata of a DetectionPrediction,
 * as well as the dimensions of the image on which we're drawing said path.
 */
export interface DetectionSVGOverlay {
    color: string; // Hexadecimal, e.g '#0088FF'
    classID: string;
    opacity: string; // Percentage, e.g '100%'
    display: string; // 'inline' or 'none'
    path: string; // The SVG path proper
    index: number;
}

/** A subset of DetectionPredictionSVGOverlay used to emit control events from the detection predictions component. */
export interface DetectionVisibilityInfo {
    classID: string;
    visible: boolean;
    opacity: number;
}

export interface ClassControl {
    id: string;
    name: string;
    color: string;
    count: number;
    visible: boolean;
    opacity: number;
    showOpacityControl: boolean;
}

export interface DetectionPredictionMouseOver {
    mouseOverPredictions: DetectionPrediction[];
    x: number;
    y: number;
}

@Injectable()
export class InferenceProvider extends BaseProvider {
    constructor(
        public api: API,
        public classProvider: ClassProvider,
        public frameProvider: FrameProvider,
        private translate: TranslateService
    ) {
        super();
    }

    public getPredictionStars(confidence: number): number {
        if (confidence >= 0.8) {
            return 5;
        } else if (confidence >= 0.6) {
            return 4;
        } else if (confidence >= 0.4) {
            return 3;
        } else if (confidence >= 0.2) {
            return 2;
        } else if (confidence >= 0.01) {
            return 1;
        } else {
            return 0;
        }
    }

    /**
     * One issue with mouse events is that they only fire for the element with the highest z-index.
     * Since we can have an arbitrary amount of overlapping detection predictions, we need a different method
     * in order to get all the predictions that might be under the user's cursor! We tag these with our own
     * custom attribute to make locating them easier
     */
    public getMouseOverPredictions(event: MouseEvent, predictions: DetectionPrediction[]): DetectionPrediction[] {
        const indicesUnderCursor = this.getIndicesUnderCursor(event);

        const currentlyHovered: DetectionPrediction[] = [];

        // Iterate through each prediction under our cursor, and add it to the list of "moused over" predictions.
        indicesUnderCursor.forEach(index => {
            if (predictions[index]) {
                currentlyHovered.push(predictions[index]);
            }
        });

        // Sorting here simplifies the logic in the detection predictions component.
        currentlyHovered.sort((a, b) => {
            return b.confidence - a.confidence;
        });

        return currentlyHovered;
    }

    /**
     * Consume the metadata we attached to each path in order to get the index of each prediction we found
     */
    private getIndicesUnderCursor(event: MouseEvent): number[] {
        const objectsUnderCursor = document
            .elementsFromPoint(event.x, event.y)
            .filter(element => element.hasAttribute('neurala:index'));

        // Get the prediction indexes we need to reference.
        if (objectsUnderCursor.length > 0) {
            const indexesUnderCursor: number[] = [];

            objectsUnderCursor.forEach(element => {
                const index = element.getAttribute('neurala:index');

                if (index !== null) {
                    indexesUnderCursor.push(parseInt(index, 10));
                }
            });

            return indexesUnderCursor;
        } else {
            return [];
        }
    }

    public async getDetectionValidationFrameDetails(
        datasetIdentifier: string,
        assetIdentifier: string,
        isTestSet: boolean = false
    ): Promise<DetectionValdiationFrameDetails> {
        const results = await this.frameProvider.getDetectionFrameDetail(datasetIdentifier, assetIdentifier, isTestSet);

        const classMap = this.classProvider.getDatasetClassNameMap(datasetIdentifier);

        return {
            name: results.name,
            thumbSignedUrl: results.thumbSignedUrl,
            signedUrl: results.signedUrl,
            groundTruths: results.groundTruths.map(gt => {
                return {
                    x1: gt.x1,
                    y1: gt.y1,
                    x2: gt.x2,
                    y2: gt.y2,
                    classObject:
                        classMap[gt.className] ||
                        this.classProvider.getFallbackClassObj(gt.className, datasetIdentifier)
                };
            }),
            predictions: results.predictions.map(p => {
                return {
                    x1: p.x1,
                    y1: p.y1,
                    x2: p.x2,
                    y2: p.y2,
                    classObject:
                        classMap[p.className] || this.classProvider.getFallbackClassObj(p.className, datasetIdentifier),
                    confidence: p.confidence
                };
            })
        };
    }

    public getDetectionSVGOverlays(boxes: DetectionBox[], width: number, height: number): DetectionSVGOverlay[] {
        if (!boxes) {
            return [];
        }

        return boxes.map((box, index) => {
            // Generate coordinate pairs for our SVG markup.
            const topLeft = [box.x1, box.y1];
            const topRight = [box.x2, box.y1];
            const bottomRight = [box.x2, box.y2];
            const bottomLeft = [box.x1, box.y2];

            return {
                index,
                color: box.classObject.color,
                classID: box.classObject.id,
                opacity: '100%',
                display: 'inline', // Default display property for an SVG element
                path: `M ${width * topLeft[0]} ${height * topLeft[1]}
                    L ${width * topRight[0]} ${height * topRight[1]}
                    ${width * bottomRight[0]} ${height * bottomRight[1]}
                    ${width * bottomLeft[0]} ${height * bottomLeft[1]}
                    z`
            };
        });
    }

    /**
     * Iterate through each prediction or ground truth box and update the display and opacity properties.
     */
    public applyVisibilityInfoToSvgOverlays(
        svgOverlays: DetectionSVGOverlay[],
        visibilityInfo: DetectionVisibilityInfo[]
    ): void {
        svgOverlays.forEach((box: any) => {
            if (visibilityInfo) {
                const classObject = visibilityInfo.find(control => {
                    return box.classID === control.classID;
                });

                if (classObject) {
                    box.opacity = `${classObject.opacity}%`;
                    box.display = classObject.visible ? 'inline' : 'none';
                }
            }
        });
    }

    public getClassControls(boxes: DetectionBox[]): ClassControl[] {
        const classControls = new Map();

        boxes.forEach(box => {
            if (!classControls.get(box.classObject.id)) {
                classControls.set(box.classObject.id, {
                    id: box.classObject.id,
                    name: box.classObject.name,
                    color: box.classObject.color,
                    count: 1,
                    visible: true,
                    opacity: 100,
                    showOpacityControl: false
                });
            } else {
                const control = classControls.get(box.classObject.id);
                control.count += 1;
                classControls.set(box.classObject.id, control);
            }
        });

        return Array.from(classControls.values());
    }

    public getDetectionVisibilityInfo(classControls: ClassControl[]): DetectionVisibilityInfo[] {
        return classControls.map(control => {
            return {
                classID: control.id,
                visible: control.visible,
                opacity: control.opacity
            };
        });
    }

    public getDetectionVisibilityIcon(classControls: ClassControl[], controlID: string): string {
        const controlIndex = this.getDetectionControlToChange(classControls, controlID);
        if (controlIndex > -1) {
            return classControls[controlIndex].visible ? 'visibility' : 'visibility_off';
        }

        return '';
    }

    /**
     * Hide all controls but the provided exception
     * This is used to allow us to open one control and automatically hide all others
     */
    public hideOtherOpacityControls(classControls: ClassControl[], exceptControlId: string): void {
        classControls.forEach(control => {
            if (control.id == exceptControlId) {
                return;
            }

            control.showOpacityControl = false;
        });
    }

    /**
     * Get the index of the class control whose property we want to modify
     */
    public getDetectionControlToChange(classControls: ClassControl[], controlID: string): number {
        return classControls.findIndex(control => control.id === controlID);
    }
}



// WEBPACK FOOTER //
// ./src/providers/inference.ts